import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Alert,
} from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import logo from "../../assets/images/Elebert-Logo.png";
import background from "../../assets/images/background.svg";
import logo360 from "../../assets/images/360-degrees 1.svg";
import { post } from "helpers/api_helper";
import * as api from "../../constants/api_constants";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tooManyAttempts, setTooManyAttempts] = useState(false); // New state for too many attempts

  // Formik validation for email and OTP
  const validation = useFormik({
    initialValues: {
      email: "",
      otp: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Please Enter Your Email"),
      otp: Yup.string().when([], {
        is: () => otpSent && !tooManyAttempts, // Only require OTP if otpSent is true and no too many attempts
        then: () => Yup.string().required("Please Enter OTP"),
        otherwise: () => Yup.string().notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      setError(null);
      setSuccess(null);

      if (!otpSent) {
        // Send OTP
        const payload = { email: values.email };
        try {
          const response = await post(api.POST_FORGOT_PASSWORD, payload);
          setSuccess(response);
          setOtpSent(true);
          setTooManyAttempts(false); // Reset too many attempts state
          console.log("response", response);
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Failed to send OTP. Please try again.";
          setError(errorMessage);
          if (errorMessage === "Too many attempts. Please try again after 10 minutes.") {
            setTooManyAttempts(true); // Set too many attempts state
            setOtpSent(false); // Hide OTP input field
          }
          console.error("Error sending OTP:", error);
        }
      } else {
        // Verify OTP
        const payload = { email: values.email, otp: values.otp };
        try {
          const response = await post(api.POST_VERIFY_OTP, payload);
          setSuccess(response);
          navigate("/reset-password", { state: { email: values.email } });
        } catch (error) {
          setError(
            error.response?.data?.message || "Invalid OTP. Please try again."
          );
          console.error("Error verifying OTP:", error);
        }
      }
    },
  });

  // Function to handle Resend OTP
  const handleResendOtp = async () => {
    setError(null);
    setSuccess(null);

    const payload = { email: validation.values.email };
    try {
      const response = await post(api.POST_FORGOT_PASSWORD, payload);
      setSuccess(response);
      setTooManyAttempts(false); // Reset too many attempts state
      console.log("resend response", response);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to resend OTP. Please try again.";
      setError(errorMessage);
      if (errorMessage === "Too many attempts. Please try again after 10 minutes.") {
        setTooManyAttempts(true); // Set too many attempts state
        setOtpSent(false); // Hide OTP input field
      }
      console.error("Error resending OTP:", error);
    }
  };

  document.title = "Forgot Password | Elbert";

  return (
    <React.Fragment>
      <div className="account-pages d-flex flex-row h-100">
        <div
          className="px-5 py-4 d-flex flex-column justify-content-between login-banner d-sm-block d-none"
          style={{
            backgroundImage: `url(${background})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "85%",
          }}
        >
          <div className="w-75 h-50 d-flex flex-column justify-content-center align-items-start text-white">
            <h1 className="my-5 d-flex justify-content-start">
              Engage <img src={logo360} alt="logo 360" />
            </h1>
            <h1 style={{ color: "white", marginBottom: "20px" }}>
              Power Up Your Day with Engage 360
            </h1>
            <p className="text-white">
              A Smarter Where Customer Relationships Are Not Just Managed, but
              Transformed into Meaningful Connections That Drive Success
            </p>
          </div>
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ marginTop: "240px" }}
          >
            <p className="text-white">Powered by Elbert Technology</p>
          </div>
        </div>
        <Container className="d-flex flex-column align-items-center justify-content-center">
          <Row className="w-50">
            <Col md={12}>
              <Card className="overflow-hidden p-0 d-flex flex-column gap-8">
                <div className="d-flex flex-column w-100 h-50 justify-content-center align-items-center">
                  <img src={logo} alt="logo" className="w-50 pb-3" />
                  <div className="text-center">
                    <h3 className="p-0 m-0 fs-4">Forgot Password</h3>
                    <p className="p-0 m-0 fs-6">Reset your password with OTP</p>
                  </div>
                </div>
                <CardBody>
                  <div className="p-4">
                    {success && <Alert color="success">{success}</Alert>}
                    {error && <Alert color="danger">{error}</Alert>}
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                      action="#"
                    >
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="email">
                          Email
                        </Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder="Enter Email"
                          type="email"
                          id="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          disabled={otpSent && !tooManyAttempts} // Disable only if OTP sent and not too many attempts
                          invalid={
                            validation.touched.email && validation.errors.email
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>
                      {otpSent && !tooManyAttempts && (
                        <div className="mb-3">
                          <Label className="form-label" htmlFor="otp">
                            OTP
                          </Label>
                          <Input
                            name="otp"
                            className="form-control"
                            placeholder="Enter OTP"
                            type="text"
                            id="otp"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.otp || ""}
                            invalid={
                              validation.touched.otp && validation.errors.otp
                            }
                          />
                          {validation.touched.otp && validation.errors.otp ? (
                            <FormFeedback type="invalid">
                              {validation.errors.otp}
                            </FormFeedback>
                          ) : null}
                          <div className="mt-2 text-center">
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleResendOtp();
                              }}
                              style={{ color: "#007bff", cursor: "pointer" }}
                            >
                              Resend OTP
                            </a>
                          </div>
                        </div>
                      )}
                      <div className="mb farmaceutica-3 row">
                        <div className="text-center">
                          <Button
                            className="btn btn-primary w-md waves-effect waves-light"
                            type="submit"
                            disabled={tooManyAttempts} // Disable button if too many attempts
                          >
                            {otpSent && !tooManyAttempts ? "Verify OTP" : "Send OTP"}
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 mb-0 row text-center">
                        <div className="col-12">
                          <Link to="/login">Back to Login</Link>
                        </div>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ForgotPassword;